// Delete my account — CLAUDE.md §4 / Qatar PDPL erasure.
//
// 🔴 What the endpoint behind this button actually does is ANONYMISE +
// TOMBSTONE, never a cascade: `families.primary_parent_user_id` and
// `refunds.invoice_id` are both ON DELETE RESTRICT, so a hard delete cannot
// even run, and forcing one would destroy the append-only money ledger and
// rewrite other people's message threads. Semantics are settled in
// docs/design-notes/m-account-delete.md — read it before changing anything
// here.
//
// The copy is honest about that: identifiers and profile data go, billing
// records stay (a legal-obligation carve-out) and are unlinked from the name.
//
// Typed confirmation, not a bare confirm(): the user must type the word, and
// the submit button stays disabled until it matches exactly.

import { useT } from '@fajrarabi/i18n/react';
import { Alert, Button, Dialog, DialogContent, Field, Input } from '@fajrarabi/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { AccountSection } from './AccountSection';

import { DELETE_CONFIRMATION_WORD, deleteAccount } from '~/lib/account';
import { ApiError } from '~/lib/api';

export function DeleteAccountCard({ locale }: { locale: string }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');

  const mutation = useMutation({
    mutationFn: () => deleteAccount(password || undefined),
    onSuccess: () => {
      // The API already cleared the auth cookies. A hard navigation is
      // deliberate: it tears down every cached query rather than letting a
      // stale /auth/me repaint a deleted account.
      if (typeof window !== 'undefined') {
        window.location.href = `/${locale}`;
      }
    },
  });

  const confirmed = typed.trim() === DELETE_CONFIRMATION_WORD;

  function close() {
    if (mutation.isPending) return;
    setOpen(false);
    setTyped('');
    setPassword('');
    mutation.reset();
  }

  return (
    <AccountSection
      id="account-danger"
      title={t('app.account.danger_title')}
      description={t('app.account.delete_description')}
      tone="danger"
    >
      <Alert tone="warning">{t('app.account.delete_kept')}</Alert>

      <div className="flex justify-end">
        <Button variant="secondary" onClick={() => setOpen(true)}>
          {t('app.account.delete_button')}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
        <DialogContent
          title={t('app.account.delete_dialog_title')}
          description={t('app.account.delete_description')}
        >
          <div className="mt-2 space-y-4">
            <Alert tone="danger">{t('app.account.delete_warning')}</Alert>

            <Field
              label={t('app.account.delete_confirm_label', {
                word: t('app.account.delete_confirm_word'),
              })}
            >
              {(id) => (
                <Input
                  id={id}
                  value={typed}
                  // The word is a fixed ASCII literal in both locales, so the
                  // field is LTR even under RTL chrome — otherwise the caret
                  // and the typed text disagree.
                  dir="ltr"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  onChange={(e) => setTyped(e.target.value)}
                />
              )}
            </Field>

            <Field label={t('app.account.delete_password_label')}>
              {(id) => (
                <Input
                  id={id}
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>

            {mutation.error && <Alert tone="danger">{messageFor(mutation.error, t)}</Alert>}

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={close} disabled={mutation.isPending}>
                {t('app.common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => mutation.mutate()}
                disabled={!confirmed || mutation.isPending}
              >
                {t('app.account.delete_submit')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AccountSection>
  );
}

function messageFor(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApiError) {
    // The API refuses while a paid subscription is live, rather than silently
    // ending someone's plan from a settings screen.
    if (err.status === 409) return t('app.account.delete_error_subscription');
    if (err.status === 401) return t('app.account.password_error_wrong');
  }
  return t('app.common.error_generic');
}
